import { Controller } from "@hotwired/stimulus"

// Drives the e-shop import picker.
//
// Selections autosave as the seller works rather than on submit: a real
// catalogue runs to thousands of rows across dozens of pages, and losing a
// page of ticks (or a rewritten description) to a stray navigation would be
// miserable. Changes are batched briefly, then POSTed as one request.
export default class extends Controller {
  static targets = [
    "row", "checkbox", "category", "selectAll",
    "bulkCategory", "selectedCount", "summary", "hint", "importButton"
  ]

  static values = {
    saveUrl: String,
    selectedLabel: String
  }

  connect() {
    this.pending = new Map()
    this.saveTimer = null
    this.syncSelectAllState()
  }

  disconnect() {
    if (this.saveTimer) clearTimeout(this.saveTimer)
  }

  // --- Row edits ---------------------------------------------------------

  rowChanged(event) {
    const row = event.target.closest("[data-row-id]")
    if (!row) return

    this.queue(row)
    this.syncSelectAllState()
    this.updateSelectedCount()
  }

  toggleAll(event) {
    const checked = event.target.checked

    this.rowTargets.forEach((row) => {
      const checkbox = row.querySelector('[data-field="selected"]')
      if (!checkbox || checkbox.disabled || checkbox.checked === checked) return

      checkbox.checked = checked
      this.queue(row)
    })

    this.updateSelectedCount()
    this.save()
  }

  // Bulk category is a convenience for the common case: a collection filter
  // showing one product family. It only touches rows the seller has already
  // ticked, so it can't quietly categorise something they didn't want.
  applyBulkCategory(event) {
    const categoryId = event.target.value
    if (!categoryId) return

    this.rowTargets.forEach((row) => {
      const checkbox = row.querySelector('[data-field="selected"]')
      const select = row.querySelector('[data-field="category_id"]')
      if (!checkbox || !select || checkbox.disabled || !checkbox.checked) return

      select.value = categoryId
      select.classList.remove("border-red-400")
      this.queue(row)
    })

    event.target.value = ""
    this.save()
  }

  // --- Saving ------------------------------------------------------------

  queue(row) {
    const id = row.dataset.rowId
    const payload = {}

    row.querySelectorAll("[data-field]").forEach((field) => {
      payload[field.dataset.field] =
        field.type === "checkbox" ? (field.checked ? "1" : "0") : field.value
    })

    this.pending.set(id, payload)
    this.scheduleSave()
  }

  scheduleSave() {
    if (this.saveTimer) clearTimeout(this.saveTimer)
    this.saveTimer = setTimeout(() => this.save(), 700)
  }

  async save() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer)
      this.saveTimer = null
    }
    if (this.pending.size === 0) return

    // Take the batch before awaiting so edits made mid-flight are not lost —
    // they land in a fresh map and get their own save.
    const batch = {}
    this.pending.forEach((payload, id) => { batch[id] = payload })
    this.pending.clear()

    this.setHint(this.element.dataset.hintSaving)

    try {
      const response = await fetch(this.saveUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ imported_products: batch })
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()
      this.applyServerCounts(data)
      this.setHint(this.element.dataset.hintSaved)
    } catch (error) {
      // Put the batch back so the next change retries it rather than dropping
      // the seller's work on a flaky connection. Edits made since the failed
      // send win over the stale batch.
      Object.entries(batch).forEach(([id, payload]) => {
        if (!this.pending.has(id)) this.pending.set(id, payload)
      })
      this.setHint(this.element.dataset.hintError)
    }
  }

  applyServerCounts(data) {
    if (typeof data.selected === "number") {
      if (this.hasSelectedCountTarget) {
        this.selectedCountTarget.textContent = data.selected
      }
      if (this.hasSummaryTarget) {
        this.summaryTarget.textContent =
          this.selectedLabelValue.replace("__count__", data.selected)
      }
    }

    // The server rendered the button's disabled state at page-load time; as
    // picks autosave, "ready" moves and the button must follow without a
    // page refresh.
    if (typeof data.ready === "number" && this.hasImportButtonTarget) {
      this.importButtonTarget.disabled = data.ready === 0
    }
  }

  // --- Local UI state ----------------------------------------------------

  updateSelectedCount() {
    if (!this.hasSummaryTarget) return

    const onPage = this.checkboxTargets.filter((c) => c.checked && !c.disabled).length
    this.summaryTarget.textContent =
      this.selectedLabelValue.replace("__count__", onPage)
  }

  syncSelectAllState() {
    if (!this.hasSelectAllTarget) return

    const usable = this.checkboxTargets.filter((c) => !c.disabled)
    const checked = usable.filter((c) => c.checked)

    this.selectAllTarget.checked = usable.length > 0 && checked.length === usable.length
    this.selectAllTarget.indeterminate = checked.length > 0 && checked.length < usable.length
  }

  setHint(text) {
    if (this.hasHintTarget && text) this.hintTarget.textContent = text
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
